import { useEffect, useRef, useState, useMemo } from 'react';

import { Container } from '@hivebrite/design-system/Container';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import Feed from 'hb-react/frontoffice/apps/LiveFeed/components/Feed';
import TopPosts from 'hb-react/frontoffice/apps/LiveFeed/components/TopPosts';
import {
  NETWORK_FEED_TYPE,
  GROUP_FEED_TYPE,
} from 'hb-react/frontoffice/apps/LiveFeed/constants/FeedType';
import * as PostApiTypes from 'hb-react/frontoffice/apps/LiveFeed/constants/PostApiTypes';
import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { useTopPostsContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/TopPostsContext';
import fetchFeedItems from 'hb-react/frontoffice/apps/LiveFeed/utils/fetchFeedItems';
import { useMultiTabsLiveFeedContext } from 'hb-react/frontoffice/apps/MultiTabsLiveFeed/contexts/MultiTabsLiveFeedContext';
import Grid, { Row, Col } from 'hb-react/shared/components/DesignSystem/Grid';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';

import CreateContent from '../CreateContent';

const LiveFeedApp = () => {
  const { config, amplitudeConfig } = useLiveFeedContext();
  const { onTriggerTopPost, topPosts, version } = useTopPostsContext();
  const multiTabsLiveFeedContext = useMultiTabsLiveFeedContext();

  const { type: configType } = config;

  const amplitude = amplitudeLogger(amplitudeConfig);

  const breakpoint = useBreakpoint();

  const [userCanAccessLiveFeed, setUserCanAccessLiveFeed] = useState(true);

  const feedRef = useRef();
  // to remove with `flag-ujb-lf-pagination` cleanup
  const scrollerRef = useRef();

  // needed to get the last top posts state
  const topPostsRef = useRef([]);

  const displayWidgets = useMemo(
    () => breakpoint === 'l' || breakpoint === 'xl',
    [breakpoint],
  );

  const userCanNotAccessLiveFeed = () => {
    setUserCanAccessLiveFeed(false);
    multiTabsLiveFeedContext?.setUserCanAccessLiveFeed(false);
  };

  const handleFetchFeedItems = async (props) => {
    const { total, items } = await fetchFeedItems(props);

    const newItems = items?.map((item) => {
      // this had to be done by hand as the api does not return instant updated data due to ES
      const topPostItem = topPostsRef.current.find(
        (topPost) => topPost.id === item.id,
      );

      if (topPostItem) {
        return {
          ...item,
          pinned_to_top_posts: true,
        };
      }

      return {
        ...item,
        pinned_to_top_posts: false,
      };
    });

    return { total, items: newItems };
  };

  const handleSubmit = (newItem) => {
    let contentType;

    switch (newItem.type) {
      case PostApiTypes.TRIP:
        contentType = 'Trip';
        break;
      case PostApiTypes.CHECKIN:
        contentType = 'CheckIn';
        break;
      case PostApiTypes.POST:
      default:
        contentType = 'UserPost';
        break;
    }

    amplitude.track('LiveFeed|PostCreated', {
      imagesNb: newItem?.attached_images?.length,
      filesNb: newItem?.uploads?.length,
      contentType,
    });

    // We do not fetch new/last item when user create content, just displaying his new post at the very top of the Feed
    return feedRef.current.setCurrentNewItem(newItem);
  };

  const showTopPosts =
    userCanAccessLiveFeed && configType === NETWORK_FEED_TYPE;

  useEffect(() => {
    topPostsRef.current = topPosts;
  }, [topPosts]);

  return (
    <>
      {showTopPosts && <TopPosts />}
      {userCanAccessLiveFeed && (
        <Container mt={showTopPosts && topPosts.length > 0 ? 2 : 6}>
          <Grid
            width="100%"
            mx={{
              _: 0,
              m: 6,
              l: 8,
            }}
          >
            <Row>
              <Col
                role="region"
                aria-label={s__('LiveFeed|Live feed')}
                data-testid="live-feed-app"
                offset={({ numCols }) => ({
                  _: 0,
                  xl: 1 / numCols,
                })}
                width={({ numCols }) => ({
                  _: 12 / numCols,
                  l: 7 / numCols,
                  xl: 6 / numCols,
                })}
              >
                <CreateContent onCreate={handleSubmit} />
                <Feed
                  ref={feedRef}
                  displayFilters={
                    config.type === NETWORK_FEED_TYPE
                      ? true
                      : config.type === GROUP_FEED_TYPE &&
                        config.topic?.tags?.length > 0
                  }
                  fetchItems={handleFetchFeedItems}
                  version={version}
                  onTriggerTopPost={onTriggerTopPost}
                  handleUserCanNotAccessLiveFeed={userCanNotAccessLiveFeed}
                  scrollerRef={scrollerRef}
                  shouldTriggerAmplitudeOnLike
                />
              </Col>
              {displayWidgets && (
                <Col
                  role="complementary"
                  width={({ numCols }) => ({
                    _: 0,
                    m: 0,
                    l: 5 / numCols,
                    xl: 4 / numCols,
                  })}
                >
                  {config.sidebar}
                </Col>
              )}
            </Row>
          </Grid>
        </Container>
      )}
    </>
  );
};

export default LiveFeedApp;
